/**
 * O desfecho de um OAuth, como o callback o devolve na URL de ``/channels``.
 *
 * - Instagram: ``?channel=<slug>&platform=instagram&result=<código>``, com o
 *   código de uma lista fechada do backend (``connected``, ``denied``…).
 * - YouTube e TikTok (legado): só ``?error=<código>`` quando falham, sem dizer
 *   a plataforma; o sucesso volta sem nada, e não há o que mostrar.
 *
 * Até aqui o ``validateChannelOnly`` descartava tudo menos o canal, e o erro do
 * callback nunca aparecia. O texto da tela sai desta tabela, nunca da URL.
 *
 * Lógica pura, fora do componente, para ser testada sem DOM.
 */
import type { ChannelsSearch, OAuthCallbackPlatform } from '@/app/routes/_search'

export type OAuthNoticeTone = 'success' | 'warning' | 'danger'

export interface OAuthNotice {
  tone: OAuthNoticeTone
  title: string
  body: string
  /**
   * Sucesso que a URL sozinha não prova: qualquer um monta um link com
   * ``result=connected``. A tela relê o destino e só mostra o aviso com a conta
   * ativa (ver :func:`confirmedNotice`).
   */
  verify?: boolean
}

const PLATFORM_LABELS: Record<OAuthCallbackPlatform, string> = {
  youtube: 'YouTube',
  tiktok: 'TikTok',
  instagram: 'Instagram',
}

/** Os códigos do callback do Instagram (lista fechada do backend). */
export const INSTAGRAM_RESULTS = [
  'connected',
  'denied',
  'missing_permission',
  'not_professional',
  'already_linked',
  'invalid_state',
  'exchange_failed',
] as const

function retryHint(label: string): string {
  return `Comece de novo pelo botão Conectar do ${label}, nesta tela.`
}

function resultNotice(platform: OAuthCallbackPlatform, code: string, allowSuccess: boolean): OAuthNotice | null {
  const label = PLATFORM_LABELS[platform]
  switch (code) {
    case 'connected':
      // Só o callback do Instagram manda ``result``; o sucesso ainda passa pela
      // releitura do destino. Fora disso, um "conectado" na URL não vale nada.
      if (!allowSuccess || platform !== 'instagram') return null
      return {
        tone: 'success',
        title: 'Instagram conectado',
        body: 'Para publicar lá, ligue o destino no Scheduler: editar o canal → aba Destinos.',
        verify: true,
      }
    case 'denied':
    case 'access_denied':
      return {
        tone: 'warning',
        title: `Você não autorizou o Prism no ${label}`,
        body: `Nada foi conectado. Se foi sem querer, comece de novo pelo botão Conectar do ${label}, nesta tela.`,
      }
    case 'missing_permission':
      return {
        tone: 'warning',
        title: 'Faltou permissão',
        body: `O ${label} não liberou todas as permissões que o Prism pede. Conecte de novo e deixe todas marcadas na tela de autorização.`,
      }
    case 'not_professional':
      return {
        tone: 'warning',
        title: 'A conta do Instagram é pessoal',
        body:
          'O Prism só publica em conta profissional. No app do Instagram, mude para conta de Criador ou Empresa ' +
          '(Configurações → Tipo de conta e ferramentas) e conecte de novo.',
      }
    case 'already_linked':
      return {
        tone: 'danger',
        title: `Essa conta do ${label} já está ligada a outro canal`,
        body: 'Uma conta serve a um canal só. Desconecte-a do outro canal, na tela dele, antes de conectar aqui.',
      }
    case 'invalid_state':
      return {
        tone: 'warning',
        title: 'O pedido de conexão expirou',
        body: `Ele vale 15 minutos e só no navegador que o começou. ${retryHint(label)}`,
      }
    case 'no_code':
    case 'exchange_failed':
      return {
        tone: 'danger',
        title: `O ${label} não confirmou a conexão`,
        body: `Nada foi conectado. Tente de novo em alguns minutos. ${retryHint(label)}`,
      }
    default:
      return {
        tone: 'danger',
        title: `A conexão com o ${label} não foi concluída`,
        body: `Código do erro: ${code}. ${retryHint(label)}`,
      }
  }
}

/** Erro legado (YouTube/TikTok): a URL não diz de qual plataforma ele veio. */
function legacyErrorNotice(code: string): OAuthNotice {
  switch (code) {
    case 'access_denied':
      return {
        tone: 'warning',
        title: 'A conexão foi recusada na tela de autorização',
        body: 'Nada foi conectado. Se foi sem querer, comece de novo pelo botão Conectar.',
      }
    case 'invalid_state':
      return {
        tone: 'warning',
        title: 'O pedido de conexão expirou',
        body: 'Ele vale 15 minutos e só no navegador que o começou. Comece de novo pelo botão Conectar.',
      }
    case 'no_code':
    case 'exchange_failed':
      return {
        tone: 'danger',
        title: 'A plataforma não confirmou a conexão',
        body: 'Nada foi conectado. Tente de novo em alguns minutos, pelo botão Conectar.',
      }
    default:
      return {
        tone: 'danger',
        title: 'A conexão não foi concluída',
        body: `Código do erro: ${code}. Comece de novo pelo botão Conectar.`,
      }
  }
}

/** A mensagem do desfecho na URL, ou ``null`` quando não houve callback. */
export function oauthCallbackNotice(search: ChannelsSearch): OAuthNotice | null {
  if (search.result) return resultNotice(search.platform ?? 'instagram', search.result, true)
  if (search.error) {
    return search.platform ? resultNotice(search.platform, search.error, false) : legacyErrorNotice(search.error)
  }
  return null
}

/** O que a releitura do destino diz da conta, depois da volta do OAuth. */
export type ConnectionCheck = 'pending' | 'active' | 'inactive'

/**
 * ``fresh``: a leitura é posterior à volta do callback (a do cache não prova
 * nada); ``accountStatus``: o status da conta do Instagram nela.
 */
export function connectionCheck(fresh: boolean, accountStatus: string | null | undefined): ConnectionCheck {
  if (!fresh) return 'pending'
  return accountStatus === 'active' ? 'active' : 'inactive'
}

const UNCONFIRMED: OAuthNotice = {
  tone: 'warning',
  title: 'A conexão com o Instagram não se confirmou',
  body:
    'O retorno dizia que a conta foi conectada, mas ela não aparece ativa neste canal. ' +
    'Comece de novo pelo botão Conectar do Instagram, nesta tela.',
}

/**
 * O aviso a mostrar depois da releitura: o sucesso só com a conta ativa (um
 * link forjado com ``result=connected`` não mostra "conectado"); enquanto a
 * releitura não chega, nada. Os outros avisos não dependem dela.
 */
export function confirmedNotice(notice: OAuthNotice, check: ConnectionCheck): OAuthNotice | null {
  if (!notice.verify) return notice
  if (check === 'pending') return null
  return check === 'active' ? notice : UNCONFIRMED
}

/** Se a URL traz algo do callback para ser lido e depois tirado. */
export function hasOAuthCallback(search: ChannelsSearch): boolean {
  return Boolean(search.result || search.error || search.platform)
}
